/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Typography, Button, Alert, Spin, message } from 'antd';
import { DownloadOutlined, CreditCardOutlined } from '@ant-design/icons';
import { IoIosCloseCircle } from 'react-icons/io';
import DocumentUnavailable from './DocumentUnavailable';

const { Text } = Typography;

type DocumentType = 'invoice' | 'receipt' | 'authorization';

type Props = {
  open: boolean;
  onClose: () => void;
  fileUrl?: string | null;
  number?: string | null;
  createdAt?: string | null;
  documentType: DocumentType;
  document?: any;
  onInvoicePay?: (invoice: any) => void;
  unavailableMessage?: string | null;
};

const DocumentPreviewDrawer: React.FC<Props> = (props) => {
  const {
    open,
    onClose,
    fileUrl,
    number,
    createdAt,
    documentType,
    document: doc,
    onInvoicePay,
    unavailableMessage,
  } = props;

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfData, setPdfData] = useState<ArrayBuffer | null>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [mounted, setMounted] = useState<boolean>(false);

  const offcanvasRef = useRef<HTMLDivElement | null>(null);
  const backdropRef = useRef<HTMLDivElement | null>(null);

  const labelMap: Record<DocumentType, string> = {
    invoice: 'Invoice',
    receipt: 'Receipt',
    authorization: 'License/Permit',
  };

  const docLabel = labelMap[documentType];
  const status = doc?.status ?? null;

  const isPaid: boolean =
    documentType === 'invoice' && status
      ? String(status).toLowerCase() === 'paid'
      : false;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (open) {
      window.document.body.style.overflow = 'hidden';
      setIsVisible(true);

      window.requestAnimationFrame(() => {
        const offcanvas = offcanvasRef.current;
        const backdrop = backdropRef.current;
        if (offcanvas && backdrop) {
          offcanvas.classList.add('show');
          backdrop.classList.add('show');
        }
      });
    } else {
      const offcanvas = offcanvasRef.current;
      const backdrop = backdropRef.current;
      if (offcanvas && backdrop) {
        offcanvas.classList.remove('show');
        backdrop.classList.remove('show');
      }
      const timer = window.setTimeout(() => {
        setIsVisible(false);
        if (typeof window !== 'undefined') {
          window.document.body.style.overflow = '';
        }
      }, 300);
      return () => window.clearTimeout(timer);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.document.body.style.overflow = '';
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open || !fileUrl) {
      setPdfData(null);
      return;
    }

    let isMounted = true;

    const fetchPdf = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(fileUrl, {
          credentials: 'include',
          method: 'GET',
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch PDF: ${response.status}`);
        }

        const arrayBuffer = await response.arrayBuffer();

        if (isMounted) {
          setPdfData(arrayBuffer);
          setError(null);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching PDF:', err);
        if (isMounted) {
          setError('Failed to load PDF for preview.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void fetchPdf();

    return () => {
      isMounted = false;
    };
  }, [open, fileUrl]);

  useEffect(() => {
    if (pdfData) {
      const url = URL.createObjectURL(
        new Blob([pdfData], { type: 'application/pdf' })
      );
      setBlobUrl(url);

      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setBlobUrl(null);
    }
  }, [pdfData]);

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined') {
        window.document.body.style.overflow = '';
      }
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  const handleDownload = () => {
    if (!fileUrl || typeof window === 'undefined') return;

    try {
      const a = window.document.createElement('a');
      const safeType = docLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      a.href = fileUrl;
      a.download = `${safeType}-${number || 'document'}.pdf`;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      message.success('Download started');
    } catch {
      message.error('Download failed');
    }
  };

  const handleInvoicePay = () => {
    if (documentType !== 'invoice') return;

    if (!doc) {
      message.error('No invoice data available');
      return;
    }

    onClose();

    if (onInvoicePay) {
      onInvoicePay(doc);
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const style = window.document.createElement('style');
    style.textContent = `
      .offcanvas-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.5);
        z-index: 1040;
        opacity: 0;
        transition: opacity 0.3s ease-in-out;
        visibility: hidden;
        pointer-events: none;
      }
      
      .offcanvas-backdrop.show {
        opacity: 1;
        visibility: visible;
        pointer-events: all;
      }
      
      .offcanvas {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        width: 800px;
        max-width: 100vw;
        background: #ffffff;
        border-left: 1px solid #dee2e6;
        box-shadow: -4px 0 16px rgba(0, 0, 0, 0.15);
        transform: translateX(100%);
        transition: transform 0.3s ease-in-out;
        z-index: 1050;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        visibility: hidden;
        pointer-events: none;
      }
      
      .offcanvas.show {
        transform: translateX(0);
        visibility: visible;
        pointer-events: all;
      }
      
      .offcanvas-header {
        padding: 10px 14px;
        border-bottom: 1px solid #dee2e6;
        background-color: #f8f9fa;
        flex-shrink: 0;
      }
      
      .offcanvas-body {
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        background: #ffffff;
        padding: 0;
      }

      .header-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      }

      .header-left {
        flex: 1;
        min-width: 0;
      }

      .invoice-details {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .header-meta-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
      }

      .header-right {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
      }

      .header-actions {
        display: flex;
        gap: 8px;
        flex-wrap: nowrap;
      }

      .close-btn {
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        color: #6c757d;
        background: transparent;
        border: none;
        cursor: pointer;
        border-radius: 4px;
        transition: background-color 0.2s;
        flex-shrink: 0;
      }

      .close-btn:hover {
        background-color: rgba(0, 0, 0, 0.05);
      }

      @media (max-width: 768px) {
        .offcanvas {
          width: 100vw;
          max-width: 100vw;
          border-left: none;
          box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.15);
        }

        .offcanvas-header {
          padding: 8px 10px;
        }

        .header-row {
          flex-direction: column;
          align-items: stretch;
        }

        .header-right {
          justify-content: space-between;
          gap: 8px;
        }

        .header-actions {
          flex: 1 1 auto;
          flex-wrap: wrap;
        }

        .header-actions .ant-btn {
          flex: 1 1 auto;
          min-width: 0;
        }
      }

      @media (max-width: 480px) {
        .offcanvas-header {
          padding: 8px;
        }

        .header-right {
          flex-direction: row;
          align-items: center;
          gap: 8px;
        }

        .header-actions {
          flex-direction: column;
        }

        .header-actions .ant-btn {
          width: 100%;
        }

        .close-btn {
          margin-left: 4px;
        }
      }
    `;
    window.document.head.appendChild(style);

    return () => {
      window.document.head.removeChild(style);
    };
  }, [docLabel]);

  if (!mounted || !isVisible || typeof window === 'undefined') {
    return null;
  }

  const content = (
    <>
      <div ref={backdropRef} className="offcanvas-backdrop" onClick={onClose} />

      <div ref={offcanvasRef} className="offcanvas">
        <div className="offcanvas-header">
          <div className="header-row">
            <div className="header-left">
              <div className="invoice-details">
                <Text strong style={{ fontSize: 18, fontWeight: 600 }}>
                  {number
                    ? `${docLabel} No: ${number}`
                    : `${docLabel} Preview`}
                </Text>

                <div className="header-meta-row">
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {createdAt
                      ? `Created: ${new Date(createdAt).toLocaleString(
                          'en-KE',
                          {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          }
                        )}`
                      : 'No date available'}
                  </Text>

                  {status && (
                    <>
                      <span style={{ color: '#d9d9d9', fontSize: 13 }}>|</span>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 12,
                          fontSize: 11,
                          fontWeight: 500,
                          backgroundColor:
                            documentType === 'invoice' && isPaid
                              ? '#f6ffed'
                              : '#fff2e8',
                          color:
                            documentType === 'invoice' && isPaid
                              ? '#52c41a'
                              : '#fa8c16',
                          border: `1px solid ${
                            documentType === 'invoice' && isPaid
                              ? '#b7eb8f'
                              : '#ffd591'
                          }`,
                        }}
                      >
                        {documentType === 'invoice' && isPaid
                          ? 'Paid'
                          : String(status)}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="header-right">
              <div className="header-actions">
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleDownload}
                  disabled={!fileUrl || loading}
                  size="middle"
                >
                  Download
                </Button>

                {documentType === 'invoice' &&
                  !isPaid &&
                  doc &&
                  onInvoicePay && (
                    <Button
                      type="primary"
                      icon={<CreditCardOutlined />}
                      onClick={handleInvoicePay}
                      size="middle"
                    >
                      Pay Invoice
                    </Button>
                  )}
              </div>

              <button
                type="button"
                className="close-btn"
                onClick={onClose}
                aria-label="Close document preview"
                style={{
                  color: '#ff4d4f',
                  border: '1px solid #ff4d4f',
                  backgroundColor: '#fff2f0'
                }}
              >
                <IoIosCloseCircle />
              </button>
            </div>
          </div>
        </div>

        <div className="offcanvas-body">
          {!fileUrl ? (
            <DocumentUnavailable
              label={docLabel}
              number={number}
              message={unavailableMessage}
            />
          ) : loading ? (
            <div
              style={{
                padding: 40,
                textAlign: 'center',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  Loading PDF preview...
                </Text>
              </div>
            </div>
          ) : error ? (
            <div
              style={{
                padding: 24,
                textAlign: 'center',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Alert
                message="Preview Unavailable"
                description={
                  <div style={{ textAlign: 'left' }}>
                    <Text style={{ fontSize: 14 }}>{error}</Text>
                    <div style={{ marginTop: 16 }}>
                      <Button
                        type="primary"
                        onClick={handleDownload}
                        size="middle"
                      >
                        Download PDF
                      </Button>
                    </div>
                  </div>
                }
                type="warning"
                showIcon
                style={{ maxWidth: 400 }}
              />
            </div>
          ) : (
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <div
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  minHeight: 0,
                }}
              >
                {blobUrl ? (
                  <iframe
                    src={blobUrl}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      display: 'block',
                    }}
                    title={`${docLabel} PDF Preview ${
                      number ? `- ${number}` : ''
                    }`}
                  />
                ) : (
                  <div
                    style={{
                      padding: 40,
                      textAlign: 'center',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Alert
                      message="Preview Error"
                      description="Failed to prepare PDF for display."
                      type="error"
                      showIcon
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(content, window.document.body);
};

export default DocumentPreviewDrawer;
